import React, { Component } from 'react';
import { Button, Checkbox, ControlLabel, FormGroup, Modal, FormControl } from 'react-bootstrap';
import { connect } from 'react-redux';

import OverlayLoader from '../components/overlayLoader';

import { showModalMessage } from '../store/modalsSlice';
import * as webViewerLicenseActions from '../store/webViewerLicensesSlice';
import { webViewerLicensesSelectors } from '../store/webViewerLicensesSlice';
import * as webViewerFeaturePackagesActions from '../store/webViewerFeaturePackagesSlice';
import { webViewerFeaturePackagesSelectors } from '../store/webViewerFeaturePackagesSlice';
import { unwrapResult } from '@reduxjs/toolkit';

const Mode = {
  Create: 0,
  Edit: 1
};

const Titles = {
  [Mode.Create]: "Create new web viewer license",
  [Mode.Edit]: "Edit web viewer license"
};

const SubmitButtonCaptions = {
  [Mode.Create]: "Create",
  [Mode.Edit]: "Save"
};

const packageDependencies = [
  {
    id: 1,
    dependencies: [],
  },
  {
    id: 2,
    dependencies: [1],
  },
  {
    id: 3,
    dependencies: [1, 2],
  },
  {
    id: 4,
    dependencies: [1, 2],
  },
  {
    id: 5,
    dependencies: [1],
  },
]

class WebViewerLicenseEditDialog extends Component {
  initState = {
    webViewerFeaturePackages: [...Array(packageDependencies.length).keys()].map(i => ++i),
    webViewerServerId: null,
    licenseCount: 1,
  };

  constructor(props) {
    super(props);

    this.state = this.initState;
    this.handleClose = this.props.onClose;
    this.onShow = this.onShow.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onSubmit = this.onSubmit.bind(this);

    this.onWebViewerFeaturePackagesChanged = this.onWebViewerFeaturePackagesChanged.bind(this);
    this.onLicenseCountChanged = this.onLicenseCountChanged.bind(this);
  }

  static get Mode() {
    return Mode;
  }

  onShow() {
    this.setState(this.initState);
    if (this.props.webViewerServerId !== undefined){
      this.setState({ webViewerServerId: this.props.webViewerServerId });
    }
    if (this.props.mode === Mode.Edit) {
      if (this.props.selectedWebViewerFeaturePackages !== undefined)
        this.setState({ webViewerFeaturePackages: this.props.selectedWebViewerFeaturePackages });
    }

    this.props.fetchWebViewerFeaturePackages()
      .then(unwrapResult)
      .catch(() => {
        this.onClose();
        this.props.showModalMessage("Error", "Can't get web viewer feature packages from server");
      });
  }

  onClose() {
    this.handleClose();
    this.setState(this.initState);
  }

  getCheckboxIdsToTurnOn(id) {
    const current = packageDependencies.find((elem) => elem.id === id);
    if (current && current.dependencies.length > 0)
      return [...current.dependencies, id];
    return [id];
  }

  onWebViewerFeaturePackagesChanged(checked, featurePackageId) {
    if (checked) {
      const ids = this.getCheckboxIdsToTurnOn(featurePackageId);
      if (ids) {
        this.setState((prevState) => {
          const resultIds = ids
            .concat(prevState.webViewerFeaturePackages)
            .filter((item, pos, arr) => arr.indexOf(item) === pos)
          return {
            webViewerFeaturePackages: resultIds   
          }
        })
      }
    }
    else {
      let isNeedToUpdate = true;
      this.state.webViewerFeaturePackages.forEach((checkedFeaturePackageId) => {
        const packageDependency = packageDependencies.find((elem) => elem.id === checkedFeaturePackageId);
        if (packageDependency && packageDependency.dependencies.includes(featurePackageId)) {
          isNeedToUpdate = false;
          return;
        }
      })
      if (isNeedToUpdate) {
        this.setState({
          webViewerFeaturePackages: this.state.webViewerFeaturePackages.filter(selectedFeaturePackageId => selectedFeaturePackageId !== featurePackageId)
        })
      }
    }
  }

  onLicenseCountChanged(count) {
    const re = /^\d+$/;
    if (count.length === 0 || !re.test(count)) {
      this.setState({ licenseCount: null });
      return;
    }
    this.setState({ licenseCount: parseInt(count, 10) });
  }

  isLoading() {
    return this.props.isWebViewerFeaturePackagesFetching;
  }

  getLoaderMessage() {
    if (this.props.isWebViewerFeaturePackagesFetching) {
      return "Web viewer feature packages loading...";
    }
  }

  isWebViewerFeaturePackagesVisible() {
    return this.props.webViewerFeaturePackages !== null && this.props.webViewerFeaturePackages.length > 0;
  }

  isSubmitButtonDisabled() {
    return this.isLoading() ||
      (this.props.mode === Mode.Edit && this.isFeatArrayEquals(this.state.webViewerFeaturePackages, this.props.selectedWebViewerFeaturePackages)) || 
      (this.props.mode === Mode.Create && this.state.licenseCount <= 0);
  }

  isFeatArrayEquals(r, l) {
    if (r.length !== l.length) {
      return false;
    }
    r.sort();
    l.sort();
    for (let i = 0; i < r.length; ++i) {
      if (r[i] !== l[i]) {
        return false;
      }
    }
    return true;
  }

  filterFeaturePackages = (featurePackage) => {
    if (this.props.mode === Mode.Create) {
      return !featurePackage.isDeprecated;
    }
    else if (this.props.mode === Mode.Edit) {
      return this.state.webViewerFeaturePackages.includes(featurePackage.id) || !featurePackage.isDeprecated;
    }
    return true;
  }

  onSubmit() {
    this.onClose();
    let submitPromise = null;
    const { webViewerServerId, webViewerFeaturePackages, licenseCount } = this.state;
    const { webViewerLicenseId } = this.props;
    switch (this.props.mode) {
      case Mode.Create:
        submitPromise = this.props.createNewWebViewerLicense({
          serverId: webViewerServerId,
          featurePackages: webViewerFeaturePackages,
          count: licenseCount
        });
        break;
      case Mode.Edit:
        submitPromise = this.props.updateWebViewerLicense({
          licenseId: webViewerLicenseId, featurePackages: webViewerFeaturePackages
        });
        break;
      default:
        submitPromise = null;
    }

    if (submitPromise == null) {
      this.props.showModalMessage('Error', 'Unknown dialog mode');
      return;
    }

    return submitPromise
      .then(unwrapResult)
      .then(lics => {
        if (this.props.mode === Mode.Create)
          return this.props.showModalMessage(
        `Web viewer license${lics.length > 1 ? 's' : ''} created`, `${lics.map(lic => lic.key).join('\n')}`
      )})
      .catch((e) => {
        console.log(e);
        console.log(this.props.webViewerLicenses.creatingUpdatingError);
        this.props.showModalMessage("Error", this.props.webViewerLicenses.creatingUpdatingError.toString());
      });
  }

  render() {
    const featurePackages = this.props.webViewerFeaturePackages.filter(this.filterFeaturePackages);

    return (
      <Modal bsSize="small" show={this.props.show} onShow={this.onShow} onHide={this.onClose} backdrop="static">
        <Modal.Header>
          <Modal.Title>{Titles[this.props.mode]}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <OverlayLoader active={this.isLoading()} text={this.getLoaderMessage()}>
            {this.isWebViewerFeaturePackagesVisible() && (
              <FormGroup controlId="fcWebViewerFeaturePackages" disabled>
                <ControlLabel>Feature packages</ControlLabel>
                {featurePackages.map(featurePackage => (
                  <Checkbox
                    key={featurePackage.id}
                    onChange={(e) => this.onWebViewerFeaturePackagesChanged(e.target.checked, featurePackage.id)}
                    checked={this.state.webViewerFeaturePackages.includes(featurePackage.id)}
                    disabled={featurePackage.isDeprecated}>
                      {featurePackage.name}
                    </Checkbox>
                ))}
              </FormGroup>
            )}
            {this.props.mode === Mode.Create ? (
                    <FormGroup controlId="fcLicenseCount">
                      <ControlLabel>Web viewer license count</ControlLabel>
                      <FormControl value={this.state.licenseCount} type="number" min="1"
                                   onChange={e => this.onLicenseCountChanged(e.target.value)} />
                    </FormGroup>
                ) : null}
          </OverlayLoader>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={this.onSubmit} disabled={this.isSubmitButtonDisabled()}>
            {SubmitButtonCaptions[this.props.mode]}
          </Button>
          <Button onClick={this.onClose}>Close</Button>
        </Modal.Footer>
      </Modal>  
    );
  }
}

const mapStateToProps = (state) => ({
  webViewerLicenses: webViewerLicensesSelectors.state(state),

  isWebViewerFeaturePackagesFetching: webViewerFeaturePackagesSelectors.isFetching(state),
  webViewerFeaturePackages: webViewerFeaturePackagesSelectors.selectAll(state),
  webViewerFeaturePackagesFetchingError: webViewerFeaturePackagesSelectors.fetchingError(state),
});

const actionCreators = {
  fetchWebViewerFeaturePackages: webViewerFeaturePackagesActions.fetchAllWebViewerFeaturePackages,

  createNewWebViewerLicense: webViewerLicenseActions.create,
  updateWebViewerLicense: webViewerLicenseActions.update,

  showModalMessage: (title, text) => showModalMessage({ title, text }),
}

export default connect(mapStateToProps, actionCreators)(WebViewerLicenseEditDialog);


// WEBPACK FOOTER //
// ./src/containers/webViewerLicenseEditDialog.js